<template>
  <v-sheet color="grey600" class="py-6 px-8 rounded-0">
    <v-img src="@/assets/img/scrap-pile.png" />

    <i18n
      path="page.faucet.title" tag="h4"
      class="text-h6 text-uppercase text-center font-weight-extrabold mt-n6 mb-6"
    >
      <scrap :amount="config.amount" class="mr-1" />
      <span v-text="config.cooldown / 60 / 1000" />
    </i18n>

    <p v-t="'page.faucet.description'" class="text-body-2 text-center" />

    <ul class="text-body-2 lh-1-5 mb-6 text--secondary">
      <i18n path="page.faucet.req.balance" tag="li">
        <scrap :amount="config.amount" class="mr-1" />
      </i18n>
      <i18n v-if="requirements.steamName" path="page.faucet.req.name" tag="li">
        <code v-text="requirements.steamName" />
      </i18n>
      <li v-if="requirements.steamMinLevel" v-text="$tc('page.faucet.req.level', requirements.steamMinLevel)" />
      <li v-if="requirements.discord">
        Join our <a
          href="http://discord.gg/banditcamp"
          class="link primary500--text font-weight-semibold"
          target="_blank"
          rel="noopener noreferrer"
        >Discord server</a>
      </li>
      <li v-text="'Steam profile & inventory MUST be public'" />
    </ul>

    <div ref="hcaptcha" class="text-center mb-2" />

    <v-btn
      v-if="discordLinked"
      large block
      depressed
      color="green800"
      class="green500--text rounded-0 font-weight-semibold"
      :ripple="false"
      :loading="loading"
      :disabled="!canUseFaucet"
      @click="claim"
    >
      <span v-if="canUseFaucet" v-t="'page.faucet.cta'" />
      <countdown
        v-else
        :duration="faucetTimeLeft / 1000"
        @finish="countdownFinished = true"
      />
    </v-btn>

    <v-btn
      v-else
      large block depressed tile
      :ripple="false"
      color="blue800 blue300--text"
      class="font-weight-semibold"
      @click="$modal.open('account', { maxWidth: 1000 }, {tab: 'settings'})"
    >
      <fai :icon="['fab', 'discord']" class="text-h6 mr-2" />
      <span>Link your Discord</span>
    </v-btn>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  props: ['close'],
  data: () => ({
    loading: false,
    countdownFinished: false,
    hcaptchaWidgetId: null,
  }),
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      user: 'user/session',
    }),
    config() {
      return this.conga.promo.faucet
    },
    requirements() {
      return this.config.requirements
    },
    discordLinked() {
      return !!this.user?.connections.find(i => i.type === 'discord')
    },
    faucetTimeLeft() {
      const lastRedeemedUnix = new Date(this.user?.faucetLastRedeemed ?? 0).getTime()
      const cooldown = this.config.cooldown || 0

      return lastRedeemedUnix + cooldown - Date.now()
    },
    canUseFaucet() {
      if (!this.faucetTimeLeft) return true
      if (this.countdownFinished) return true

      return this.faucetTimeLeft <= 0
    },
  },
  mounted() {
    const $div = this.$refs.hcaptcha
    if (!$div) return

    this.hcaptchaWidgetId = window.hcaptcha.render($div, {
      sitekey: '93f16d0e-b478-454c-a4ac-b91d80de796e',
    })
  },
  methods: {
    ...mapActions({
      claimFaucet: 'user/claimFaucet',
    }),
    async claim() {
      if (this.loading) return
      this.loading = true

      try {
        if (!this.hcaptchaWidgetId) throw new Error('InvalidCaptcha')

        const token = window.hcaptcha.getResponse(this.hcaptchaWidgetId)
        if (!token) throw new Error('InvalidCaptcha')

        const { amount } = await this.claimFaucet(token)

        const { toScrap } = this.$options.filters
        const scrap = toScrap(amount)

        this.$toast.success(
          this.$t('page.faucet.notification.text'),
          { title: this.$t('page.faucet.notification.title', [scrap]) },
        )

        this.close()
      } catch (error) {
        if (error.message === 'InvalidCaptcha') {
          window.hcaptcha.reset(this.hcaptchaWidgetId)
        }

        if (error.message === 'UserConnectionNotFound') {
          this.$toast.open(
            this.$t('errors.UserConnectionNotFound', { provider: 'Discord' }),
            { type: 'error' },
          )
        } else {
          this.$toast.error(error.message)
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  code {
    user-select: all;
  }
</style>
